'use client';

import { TTourPhoto } from '@/types';
import 'photoswipe/dist/photoswipe.css';
import { useCallback, useEffect, useState } from 'react';
import { Item, Gallery } from 'react-photoswipe-gallery';
import { ButtonSemiTransparent } from '../buttons/ButtonSemiTransparent';
import './css/lightbox.css';

interface TLightboxProps {
  images: Array<TTourPhoto>;
}

type TSize = {
  height: number;
  width: number;
};

const imageSizeCache = new Map();

// @TODO
// 1. style lightbox

export const Lightbox = ({ images }: TLightboxProps) => {
  const [imageSizes, setImageSizes] = useState<Map<string, TSize>>(new Map());

  const handleClassName = (index: number) => {
    switch (index) {
      case 0: {
        return 'tour-hero__image_main';
      }
      case 1: {
        return 'hidden lg:block tour-hero__image_aside-one';
      }
      case 2: {
        return 'hidden lg:block tour-hero__image_aside-two';
      }
      default: {
        return 'hidden';
      }
    }
  };

  const getSize = useCallback(async (image: TTourPhoto) => {
    const cachedSize = imageSizeCache.get(image.url);
    if (cachedSize) return cachedSize;

    return new Promise<TSize>((resolve) => {
      const img = new Image();
      img.src = image.url;
      img.onload = () => {
        const size = { width: img.naturalWidth, height: img.naturalHeight };
        imageSizeCache.set(image.url, size);
        resolve(size);
      };
      img.onerror = () => {
        const size = { width: 0, height: 0 };
        imageSizeCache.set(image.url, size);
        resolve(size);
      };
    });
  }, []);

  useEffect(() => {
    const fetchImageSizes = async () => {
      const sizes = await Promise.all(images.map((img) => getSize(img)));
      const sizeMap = new Map(images.map((img, index) => [img.url, sizes[index]]));
      setImageSizes(sizeMap);
    };

    fetchImageSizes();
  }, [images, getSize]);

  return (
    <div className="tour-hero__container lg:tour-hero__container__large">
      <Gallery>
        {images.map((photo, index) => {
          const { width, height } = imageSizes.get(photo.url) || { width: 0, height: 0 };
          return (
            <Item<HTMLImageElement> key={photo.id} original={photo.url} width={width} height={height} alt="">
              {({ ref, open }) => (
                <img className={handleClassName(index)} src={photo.url} ref={ref} onClick={open} alt="" />
              )}
            </Item>
          );
        })}
        {images && images.length > 3 && (
          <ButtonSemiTransparent className="see-all__btn font-inter">See all photos</ButtonSemiTransparent>
        )}
      </Gallery>
    </div>
  );
};
